import { Box, Divider } from '@mui/material';
import React from 'react';
import {
  createNextYAxis,
  upgradePlotPreferences,
} from '../config/plotPreferences';
import {
  AxisKey,
  AxisPreferences,
  CurveId,
  CurvePreferences,
  CurveRelation,
  ThresholdPrefs,
  TimeseriesAxisLabelMode,
  ToolBarPreferences,
} from '../config/plotTypes';
import { AxisManagement } from './AxisManagement/AxisManagement';
import { CurveSettings } from './CurveSettings/CurveSettings';
import { RelationsGlobalSettings } from './RelationsGlobalSettings/RelationsGlobalSettings';
import { ToolBarSettings } from './ToolBarSettings/ToolBarSettings';

export type ControlPanelSection =
  | 'curveSettings'
  | 'axisManagement'
  | 'relationsGlobalSettings'
  | 'toolBarSettings';

export interface ControlPanelSettings {
  curvePrefs: Record<CurveId, CurvePreferences>;
  curveOrder?: CurveId[];
  curveToSourceIndex?: Record<CurveId, number>;
  /** IANA timezone per curve; preserved across control-panel edits. */
  curveToTimezone?: Record<CurveId, string>;
  /** Timeseries x-axis label mode; preserved across control-panel edits. */
  timeseriesAxisLabelMode?: TimeseriesAxisLabelMode;
  axisPrefs: Record<AxisKey, AxisPreferences>;
  dataSourceToAxis: Record<CurveId, AxisKey>;
  yAxisOrder: AxisKey[];
  curveRelations: CurveRelation[];
  showRangeSlider: boolean;
  thresholdPrefs?: ThresholdPrefs;
  toolBarPrefs: ToolBarPreferences;
}

export interface ControlPanelChangeMeta {
  source:
    | 'curve'
    | 'axis'
    | 'mapping'
    | 'relation'
    | 'global'
    | 'toolbar'
    | 'batch';
  reason: 'edit' | 'add' | 'delete' | 'reorder' | 'toggle' | 'normalize';
}

export interface ControlPanelProps {
  settings: ControlPanelSettings;
  onSettingsChanged: (
    next: ControlPanelSettings,
    meta: ControlPanelChangeMeta
  ) => void;
  plotType?: 'timeseries' | 'scatter';
  hide?: boolean;
  visibleSections?: ControlPanelSection[];
  useRangeSliderControl?: boolean;
}

const ALL_SECTIONS: ControlPanelSection[] = [
  'curveSettings',
  'axisManagement',
  'relationsGlobalSettings',
  'toolBarSettings',
];

const moveItemToStart = <T,>(items: T[], item: T): T[] => [
  item,
  ...items.filter((value) => value !== item),
];

const moveItemToEnd = <T,>(items: T[], item: T): T[] => [
  ...items.filter((value) => value !== item),
  item,
];

function reorderFromOrderedKeys<K extends string, V>(
  input: Record<K, V>,
  orderedKeys: K[]
): Record<K, V> {
  const output = {} as Record<K, V>;
  orderedKeys.forEach((key) => {
    if (input[key] !== undefined) {
      output[key] = input[key];
    }
  });
  return output;
}

const shouldBlockPinnedReorder = (
  sourceIndex: number,
  destinationIndex: number,
  pinnedIndex: number
): boolean =>
  pinnedIndex !== -1 &&
  (sourceIndex === pinnedIndex || destinationIndex === pinnedIndex);

const normalizeSettings = (
  settings: ControlPanelSettings,
  plotType: 'timeseries' | 'scatter'
): ControlPanelSettings => {
  const resolvedSettings = upgradePlotPreferences(settings);
  const axisKeys = Object.keys(resolvedSettings.axisPrefs) as AxisKey[];
  const xAxisKey = axisKeys.find(
    (axisKey) => resolvedSettings.axisPrefs[axisKey]?.isXAxis
  );
  const orderedAxisKeys =
    plotType === 'scatter' && xAxisKey
      ? moveItemToEnd(axisKeys, xAxisKey)
      : axisKeys;
  const normalizedAxisPrefs = reorderFromOrderedKeys(
    resolvedSettings.axisPrefs,
    orderedAxisKeys
  ) as Record<AxisKey, AxisPreferences>;

  const yAxisKeys = axisKeys.filter(
    (axisKey) => !normalizedAxisPrefs[axisKey]?.isXAxis
  );

  const orderedYAxes = resolvedSettings.yAxisOrder.filter((axisKey) =>
    yAxisKeys.includes(axisKey)
  );

  yAxisKeys.forEach((axisKey) => {
    if (!orderedYAxes.includes(axisKey)) {
      orderedYAxes.push(axisKey);
    }
  });

  const firstExistingAxis = axisKeys[0];
  const fallbackAxis = orderedYAxes[0] ?? firstExistingAxis;
  const curveKeys = Object.keys(resolvedSettings.curvePrefs) as CurveId[];
  const orderedCurves = (resolvedSettings.curveOrder ?? []).filter((curveKey) =>
    curveKeys.includes(curveKey)
  );

  curveKeys.forEach((curveKey) => {
    if (!orderedCurves.includes(curveKey)) {
      orderedCurves.push(curveKey);
    }
  });

  const xCurveId = xAxisKey
    ? curveKeys.find(
        (curveKey) => resolvedSettings.dataSourceToAxis[curveKey] === xAxisKey
      )
    : undefined;

  if (xCurveId && orderedCurves.includes(xCurveId)) {
    const pinnedCurveOrder =
      plotType === 'scatter'
        ? moveItemToEnd(orderedCurves, xCurveId)
        : moveItemToStart(orderedCurves, xCurveId);
    orderedCurves.splice(0, orderedCurves.length, ...pinnedCurveOrder);
  }

  const normalizedCurvePrefs = xCurveId
    ? (reorderFromOrderedKeys(
        resolvedSettings.curvePrefs,
        plotType === 'scatter'
          ? moveItemToEnd(curveKeys, xCurveId)
          : moveItemToStart(curveKeys, xCurveId)
      ) as Record<CurveId, CurvePreferences>)
    : resolvedSettings.curvePrefs;

  const normalizedMapping: Record<CurveId, AxisKey> = {};
  const normalizedCurveToSourceIndex: Record<CurveId, number> = {};
  curveKeys.forEach((curveKey) => {
    const mappedAxis = resolvedSettings.dataSourceToAxis[curveKey];
    if (mappedAxis && normalizedAxisPrefs[mappedAxis]) {
      normalizedMapping[curveKey] = mappedAxis;
    } else if (fallbackAxis) {
      normalizedMapping[curveKey] = fallbackAxis;
    }

    const sourceIndex = resolvedSettings.curveToSourceIndex?.[curveKey];
    if (sourceIndex !== undefined) {
      normalizedCurveToSourceIndex[curveKey] = sourceIndex;
    }
  });

  const orderedSourceKeys = xCurveId
    ? plotType === 'scatter'
      ? moveItemToEnd(curveKeys, xCurveId)
      : moveItemToStart(curveKeys, xCurveId)
    : curveKeys;
  const normalizedCurveToSourceIndexOrdered = reorderFromOrderedKeys<
    CurveId,
    number
  >(normalizedCurveToSourceIndex, orderedSourceKeys);

  return {
    curvePrefs: normalizedCurvePrefs,
    curveOrder: orderedCurves,
    curveToSourceIndex: normalizedCurveToSourceIndexOrdered,
    curveToTimezone: resolvedSettings.curveToTimezone,
    timeseriesAxisLabelMode: resolvedSettings.timeseriesAxisLabelMode,
    axisPrefs: normalizedAxisPrefs,
    dataSourceToAxis: normalizedMapping,
    yAxisOrder: orderedYAxes,
    curveRelations: resolvedSettings.curveRelations ?? [],
    showRangeSlider: resolvedSettings.showRangeSlider,
    thresholdPrefs: resolvedSettings.thresholdPrefs,
    toolBarPrefs: resolvedSettings.toolBarPrefs,
  };
};

export const ControlPanel: React.FC<ControlPanelProps> = ({
  settings,
  onSettingsChanged,
  plotType = 'timeseries',
  hide = false,
  visibleSections,
  useRangeSliderControl = true,
}) => {
  if (hide) return null;

  const sections = new Set(visibleSections ?? ALL_SECTIONS);
  const useCurveSettings = sections.has('curveSettings');
  const useAxisManagement = sections.has('axisManagement');
  const useRelationsGlobalSettings =
    plotType === 'timeseries' && sections.has('relationsGlobalSettings');
  const useToolBarSettings = sections.has('toolBarSettings');
  const currentSettings = normalizeSettings(settings, plotType);

  const emit = (next: ControlPanelSettings, meta: ControlPanelChangeMeta) => {
    onSettingsChanged(normalizeSettings(next, plotType), meta);
  };

  return (
    <Box p={2} width={300}>
      {useCurveSettings && (
        <CurveSettings
          curves={currentSettings.curvePrefs}
          curveOrder={currentSettings.curveOrder}
          relations={currentSettings.curveRelations}
          plotType={plotType}
          axes={currentSettings.axisPrefs}
          dataSourceToAxis={currentSettings.dataSourceToAxis}
          onUpdateCurve={(key, prefs) =>
            emit(
              {
                ...currentSettings,
                curvePrefs: {
                  ...currentSettings.curvePrefs,
                  [key]: prefs,
                },
              },
              { source: 'curve', reason: 'edit' }
            )
          }
          onUpdateAxisMapping={(dataSourceKey, axisKey) =>
            emit(
              {
                ...currentSettings,
                dataSourceToAxis: {
                  ...currentSettings.dataSourceToAxis,
                  [dataSourceKey]: axisKey,
                },
              },
              { source: 'mapping', reason: 'edit' }
            )
          }
          onReorderCurve={(sourceIndex, destinationIndex) =>
            (() => {
              const baseOrder = (
                currentSettings.curveOrder ??
                (Object.keys(currentSettings.curvePrefs) as CurveId[])
              ).filter(
                (curveKey) => currentSettings.curvePrefs[curveKey] !== undefined
              );

              if (
                sourceIndex < 0 ||
                destinationIndex < 0 ||
                sourceIndex >= baseOrder.length ||
                destinationIndex >= baseOrder.length
              ) {
                return;
              }

              const xAxisKey = (
                Object.keys(currentSettings.axisPrefs) as AxisKey[]
              ).find((axisKey) => currentSettings.axisPrefs[axisKey]?.isXAxis);
              const xCurveId = xAxisKey
                ? (
                    Object.keys(currentSettings.dataSourceToAxis) as CurveId[]
                  ).find(
                    (curveId) =>
                      currentSettings.dataSourceToAxis[curveId] === xAxisKey
                  )
                : undefined;
              const xCurveIndex = xCurveId ? baseOrder.indexOf(xCurveId) : -1;

              if (
                plotType === 'scatter' &&
                shouldBlockPinnedReorder(
                  sourceIndex,
                  destinationIndex,
                  xCurveIndex
                )
              ) {
                return;
              }

              const draggedCurveId = baseOrder[sourceIndex];
              const intervalRelation = currentSettings.curveRelations.find(
                (relation) =>
                  relation.relationType === 'interval' &&
                  [
                    ...(relation.relatedCurves ?? []).slice(0, 2),
                    relation.mainCurve,
                  ].includes(draggedCurveId)
              );

              if (intervalRelation) {
                const relationGroupSet = new Set<CurveId>([
                  ...(intervalRelation.relatedCurves ?? []).slice(0, 2),
                  intervalRelation.mainCurve,
                ]);

                if (
                  plotType === 'scatter' &&
                  xCurveId &&
                  relationGroupSet.has(xCurveId)
                ) {
                  return;
                }

                const groupedCurveOrder = [
                  ...(intervalRelation.relatedCurves ?? []).slice(0, 2),
                  intervalRelation.mainCurve,
                ].filter(
                  (curveId): curveId is CurveId =>
                    relationGroupSet.has(curveId) &&
                    baseOrder.includes(curveId) &&
                    currentSettings.curvePrefs[curveId] !== undefined
                );

                if (groupedCurveOrder.length > 1) {
                  const remainingOrder = baseOrder.filter(
                    (curveId) => !relationGroupSet.has(curveId)
                  );
                  const insertAt = baseOrder
                    .slice(0, destinationIndex)
                    .filter((curveId) => !relationGroupSet.has(curveId)).length;

                  const nextCurveOrder = [
                    ...remainingOrder.slice(0, insertAt),
                    ...groupedCurveOrder,
                    ...remainingOrder.slice(insertAt),
                  ];

                  emit(
                    {
                      ...currentSettings,
                      curveOrder: nextCurveOrder,
                    },
                    { source: 'curve', reason: 'reorder' }
                  );

                  return;
                }
              }

              const nextCurveOrder = [...baseOrder];
              const [removed] = nextCurveOrder.splice(sourceIndex, 1);
              nextCurveOrder.splice(destinationIndex, 0, removed);

              emit(
                {
                  ...currentSettings,
                  curveOrder: nextCurveOrder,
                },
                { source: 'curve', reason: 'reorder' }
              );
            })()
          }
        />
      )}
      {useCurveSettings &&
        (useAxisManagement || useRelationsGlobalSettings) && (
          <Divider sx={{ my: 1.25 }} />
        )}
      {useAxisManagement && (
        <AxisManagement
          axes={currentSettings.axisPrefs}
          onUpdateAxis={(key, prefs) =>
            emit(
              {
                ...currentSettings,
                axisPrefs: {
                  ...currentSettings.axisPrefs,
                  [key]: prefs,
                },
              },
              { source: 'axis', reason: 'edit' }
            )
          }
          onReorderAxis={(sourceIndex, destinationIndex) => {
            const baseOrder = Object.keys(
              currentSettings.axisPrefs
            ) as AxisKey[];
            if (
              sourceIndex < 0 ||
              destinationIndex < 0 ||
              sourceIndex >= baseOrder.length ||
              destinationIndex >= baseOrder.length
            ) {
              return;
            }

            const xAxisIndex = baseOrder.findIndex(
              (axisKey) => currentSettings.axisPrefs[axisKey]?.isXAxis
            );

            if (
              plotType === 'scatter' &&
              shouldBlockPinnedReorder(
                sourceIndex,
                destinationIndex,
                xAxisIndex
              )
            ) {
              return;
            }

            const nextAxisOrder = [...baseOrder];
            const [removed] = nextAxisOrder.splice(sourceIndex, 1);
            nextAxisOrder.splice(destinationIndex, 0, removed);

            const reorderedAxes = reorderFromOrderedKeys(
              currentSettings.axisPrefs,
              nextAxisOrder
            ) as Record<AxisKey, AxisPreferences>;

            const orderedYAxes = (
              Object.keys(reorderedAxes) as AxisKey[]
            ).filter((axisKey) => !reorderedAxes[axisKey]?.isXAxis);

            emit(
              {
                ...currentSettings,
                axisPrefs: reorderedAxes,
                yAxisOrder: orderedYAxes,
              },
              { source: 'axis', reason: 'reorder' }
            );
          }}
          onAddAxis={() => {
            const { axisKey, axisPreferences } = createNextYAxis(
              currentSettings.axisPrefs
            );
            emit(
              {
                ...currentSettings,
                axisPrefs: {
                  ...currentSettings.axisPrefs,
                  [axisKey]: axisPreferences,
                },
                yAxisOrder: [...currentSettings.yAxisOrder, axisKey],
              },
              { source: 'axis', reason: 'add' }
            );
          }}
          onDeleteAxis={(axisKey) => {
            if (currentSettings.axisPrefs[axisKey]?.isXAxis) return;

            const remainingYAxes = (
              Object.keys(currentSettings.axisPrefs) as AxisKey[]
            ).filter(
              (key) =>
                key !== axisKey && !currentSettings.axisPrefs[key]?.isXAxis
            );
            if (remainingYAxes.length === 0) return;

            const fallbackAxis = remainingYAxes[0];
            const nextAxisPrefs = { ...currentSettings.axisPrefs };
            delete nextAxisPrefs[axisKey];

            const nextDataSourceToAxis = {
              ...currentSettings.dataSourceToAxis,
            };
            (Object.keys(nextDataSourceToAxis) as CurveId[]).forEach(
              (dataSourceKey) => {
                if (nextDataSourceToAxis[dataSourceKey] === axisKey) {
                  nextDataSourceToAxis[dataSourceKey] = fallbackAxis;
                }
              }
            );

            emit(
              {
                ...currentSettings,
                axisPrefs: nextAxisPrefs,
                yAxisOrder: currentSettings.yAxisOrder.filter(
                  (key) => key !== axisKey
                ),
                dataSourceToAxis: nextDataSourceToAxis,
              },
              { source: 'axis', reason: 'delete' }
            );
          }}
        />
      )}
      {useAxisManagement && useRelationsGlobalSettings && (
        <Divider sx={{ my: 1.25 }} />
      )}
      {useRelationsGlobalSettings && (
        <RelationsGlobalSettings
          curves={currentSettings.curvePrefs}
          axes={currentSettings.axisPrefs}
          relations={currentSettings.curveRelations}
          showRelationTypeSelector={plotType === 'timeseries'}
          onUpdateRelation={(index, relation) =>
            emit(
              {
                ...currentSettings,
                curveRelations: currentSettings.curveRelations.map((rel, i) =>
                  i === index ? relation : rel
                ),
              },
              { source: 'relation', reason: 'edit' }
            )
          }
          onAddRelation={() => {
            const curveKeys = Object.keys(currentSettings.curvePrefs);
            const mainCurve = curveKeys[0] || '';
            const relatedCurves = curveKeys.slice(1, 2);
            emit(
              {
                ...currentSettings,
                curveRelations: [
                  ...currentSettings.curveRelations,
                  {
                    relationType: 'correlation',
                    mainCurve,
                    relatedCurves,
                    color: '#8b5cf6',
                  },
                ],
              },
              { source: 'relation', reason: 'add' }
            );
          }}
          onDeleteRelation={(index) =>
            emit(
              {
                ...currentSettings,
                curveRelations: currentSettings.curveRelations.filter(
                  (_, currentIndex) => currentIndex !== index
                ),
              },
              { source: 'relation', reason: 'delete' }
            )
          }
          showRangeSlider={currentSettings.showRangeSlider}
          onToggleRangeSlider={() =>
            emit(
              {
                ...currentSettings,
                showRangeSlider: !currentSettings.showRangeSlider,
              },
              { source: 'global', reason: 'toggle' }
            )
          }
          useRangeSliderToggle={
            plotType === 'timeseries' && useRangeSliderControl
          }
        />
      )}
      {(useCurveSettings || useAxisManagement || useRelationsGlobalSettings) &&
        useToolBarSettings && <Divider sx={{ my: 1.25 }} />}
      {useToolBarSettings && (
        <ToolBarSettings
          value={currentSettings.toolBarPrefs}
          onChange={(prefs) =>
            emit(
              {
                ...currentSettings,
                toolBarPrefs: prefs,
              },
              { source: 'toolbar', reason: 'edit' }
            )
          }
        />
      )}
    </Box>
  );
};
